<template>
  <div class="sales-step-content">
    <div class="text-center mb-3">
      <font-awesome-icon icon="credit-card" size="3x" class="text-success mb-2" />
      <h4>Zahlung & Bestätigung</h4>
      <p class="text-muted mb-0">
        Bestätige deine Auswahl und wähle die Zahlungsmethode
      </p>
    </div>

    <!-- Order Summary -->
    <div class="card mb-3">
      <div class="card-header py-2">
        <h6 class="mb-0">Bestellübersicht</h6>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <div class="col-md-6">
            <h6>Ausgewählter Bereich:</h6>
            <ul class="list-unstyled">
              <li class="mb-1">
                <font-awesome-icon icon="users" class="me-2 text-primary" />
                {{ selectedTeam?.name }}
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <h6>Gewählter Plan:</h6>
            <div class="d-flex align-items-center">
              <font-awesome-icon 
                :icon="planInfo.icon" 
                class="me-2 text-warning" 
              />
              <span class="fw-bold">
                {{ planInfo.label }}
              </span>
            </div>
            <div class="mt-2" v-if="planInfo.coins !== null">
              <span class="badge bg-secondary">
                {{ planInfo.coins }} Quittungen pro Monat
              </span>
            </div>
          </div>
        </div>

        <div v-if="couponInfo.applied" class="mt-3">
          <h6>Gutschein:</h6>
          <div class="d-flex flex-column flex-md-row gap-2 align-items-start align-items-md-center">
            <span class="badge bg-success text-uppercase">{{ couponInfo.code }}</span>
            <div>
              <div>{{ couponInfo.description }}</div>
              <small v-if="couponInfo.meta" class="text-muted">{{ couponInfo.meta }}</small>
            </div>
          </div>
        </div>
        
        <hr>
        
        <div v-if="priceData.totalPrice > 0" class="row">
          <div class="col-md-6"><strong>Netto:</strong></div>
          <div class="col-md-6 text-end">{{ formatCurrency(priceData.netPrice) }}</div>
        </div>
        <div v-if="priceData.totalPrice > 0" class="row">
          <div class="col-md-6"><strong>MwSt. (19%):</strong></div>
          <div class="col-md-6 text-end">{{ formatCurrency(priceData.vatAmount) }}</div>
        </div>
        <div v-if="priceData.totalPrice === 0" class="row">
          <div class="col-md-6"><strong>Gutschein angewendet:</strong></div>
          <div class="col-md-6 text-end">
            <span class="badge bg-success">Kostenlos</span>
          </div>
        </div>
        <hr>
        <div class="row">
          <div class="col-md-6"><strong>Gesamtpreis:</strong></div>
          <div class="col-md-6 text-end">
            <div class="h4 mb-0" :class="priceData.totalPrice === 0 ? 'text-success' : 'text-primary'">
              {{ priceData.totalPrice === 0 ? 'Kostenlos' : formatCurrency(priceData.totalPrice) }}
            </div>
            <small v-if="priceData.totalPrice > 0" class="text-muted">pro Jahr</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Billing Data Summary -->
    <div v-if="hasFilledBillingData" class="card mb-3">
      <div class="card-header py-2">
        <h6 class="mb-0">Rechnungsdaten</h6>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <div
            class="col-md-6"
            v-if="fullName || billingData.company || streetLine || cityLine || billingData.country"
          >
            <h6>Rechnungsadresse:</h6>
            <ul class="list-unstyled">
              <li>
                <div v-if="fullName"><strong>{{ fullName }}</strong></div>
                <div v-if="billingData.company">{{ billingData.company }}</div>
                <div v-if="streetLine">{{ streetLine }}</div>
                <div v-if="cityLine">{{ cityLine }}</div>
                <div v-if="billingData.country">{{ billingData.country }}</div>
              </li>
            </ul>
          </div>
          <div
            class="col-md-6"
            v-if="billingData.email || billingData.taxId || billingData.vatId"
          >
            <h6>Kontaktdaten:</h6>
            <ul class="list-unstyled">
              <li>
                <div v-if="billingData.email"><strong>E-Mail:</strong> {{ billingData.email }}</div>
                <div v-if="billingData.taxId"><strong>Steuernummer:</strong> {{ billingData.taxId }}</div>
                <div v-if="billingData.vatId"><strong>USt-IdNr.:</strong> {{ billingData.vatId }}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Methods -->
    <PaymentMethods
      :price-data="priceData"
      @payment-method-changed="onPaymentMethodChanged"
    />

    <!-- Terms and Conditions -->
    <TermsAndConditions @terms-changed="onTermsChanged" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePricing } from '../composables/usePricing'
import PaymentMethods from '../components/PaymentMethods.vue'
import TermsAndConditions from '../components/TermsAndConditions.vue'

const props = defineProps({
  selectedTeam: {
    type: Object,
    default: null
  },
  selectedPlan: {
    type: Object,
    default: null
  },
  priceData: {
    type: Object,
    required: true
  },
  billingData: {
    type: Object,
    required: true
  },
  hasFilledBillingData: {
    type: Boolean,
    default: false
  },
  couponResult: {
    type: Object,
    default: null
  },
  couponCode: {
    type: [String, Object],
    default: ''
  },
  isFreeUpgrade: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['payment-method-changed', 'terms-changed'])

const { formatCurrency } = usePricing()

const normalizedCouponCode = computed(() => {
  if (props.couponCode && typeof props.couponCode === 'string') {
    return props.couponCode.trim().toUpperCase()
  }
  return ''
})

const couponInfo = computed(() => {
  if (!props.couponResult?.success || !props.couponResult.data?.coupon) {
    return { applied: false }
  }

  const coupon = props.couponResult.data.coupon
  const code = normalizedCouponCode.value || coupon.code || ''

  let description = coupon.description || 'Gutschein angewendet'
  let meta = null

  if (coupon.type === 'free_coins') {
    description = `Kostenlose ${coupon.coins || coupon.value || 0} Quittungen`
  } else if (coupon.type === 'user_related') {
    description = coupon.description || 'Benutzerspezifischer Gutschein'
  } else if (coupon.type === 'free_team_pro') {
    const planText = coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro'
    const durationUnit = coupon.teamProDurationUnit || 'months'
    const durationLabels = {
      days: 'Tage',
      months: 'Monate',
      years: 'Jahre',
    }
    const durationLabel = durationLabels[durationUnit] || durationUnit
    const durationText =
      coupon.teamProDuration === 1
        ? `1 ${durationLabel.slice(0, -1)}`
        : `${coupon.teamProDuration || 0} ${durationLabel}`
    const coinsText = coupon.teamProCoins > 0 ? ` + ${coupon.teamProCoins} Quittungen (Gesamt)` : ''
    description = `Kostenloses Team ${planText} für ${durationText}${coinsText}`
  }

  if (props.isFreeUpgrade && props.priceData.totalPrice === 0) {
    meta = 'Der Gesamtbetrag ist durch den Gutschein abgedeckt.'
  } else if (props.priceData.totalPrice > 0 && props.couponResult.data?.discountAmount) {
    meta = `Rabatt: ${formatCurrency(props.couponResult.data.discountAmount)}`
  }

  return {
    applied: true,
    code,
    description,
    meta
  }
})

const planInfo = computed(() => {
  if (props.selectedPlan) {
    return {
      label: props.selectedPlan.type === 'team_pro' ? 'Pro Plan' : 'Ultimate Plan',
      icon: props.selectedPlan.type === 'team_pro' ? 'star' : 'crown',
      coins: props.selectedPlan.teamPlanCoins ?? null
    }
  }

  const coupon = props.couponResult?.success ? props.couponResult.data?.coupon : null
  if (coupon?.type === 'free_team_pro') {
    return {
      label: coupon.teamProPlan === 'ultimate' ? 'Ultimate Plan' : 'Pro Plan',
      icon: coupon.teamProPlan === 'ultimate' ? 'crown' : 'star',
      coins: coupon.teamProCoins ?? null
    }
  }

  return {
    label: 'Plan nicht ausgewählt',
    icon: 'question-circle',
    coins: null
  }
})

const fullName = computed(() => {
  const first = props.billingData.firstName?.trim()
  const last = props.billingData.lastName?.trim()
  if (!first && !last) return ''
  return [first, last].filter(Boolean).join(' ')
})

const streetLine = computed(() => {
  const street = props.billingData.street?.trim()
  const number = props.billingData.number?.trim()
  if (!street && !number) return ''
  return [street, number].filter(Boolean).join(' ')
})

const cityLine = computed(() => {
  const zip = props.billingData.zip?.trim()
  const city = props.billingData.city?.trim()
  if (!zip && !city) return ''
  return [zip, city].filter(Boolean).join(' ')
})

const onPaymentMethodChanged = (method) => {
  emit('payment-method-changed', method)
}

const onTermsChanged = (accepted) => {
  emit('terms-changed', accepted)
}
</script>
